/**
 * Central UX sound service, backed by howler.js.
 *
 * - All sounds in the manifest are preloaded (fetched and decoded) during app initialisation,
 *   so playback is immediate (no network or decode latency at play time).
 * - Howler owns the Web Audio plumbing: a shared AudioContext, decoding, master volume, and
 *   resuming the context on the first user gesture (its autoUnlock behaviour).
 * - Multiple variations of a sound are picked at random, never repeating the previous variation.
 *
 * Feature-flag / user-preference gating is the responsibility of callers
 * (see usePlaySound.ts and soundMiddleware.ts) — this module only knows how to play.
 *
 * howler is imported dynamically in initialiseSounds, so it only loads for users with
 * sounds enabled and stays out of the main bundle.
 */
import type { Howl } from 'howler';

import globalLogger from '../logger';
import { SOUND_MANIFEST, SoundFile, SoundManifest } from './soundManifest';
import { DEFAULT_SOUND_VOLUME, MAX_SOUND_VOLUME, MIN_SOUND_VOLUME, SoundId } from './soundConstants';

const logger = globalLogger.createChannel('sounds');

type HowlerModule = typeof import('howler');

let howlerModule: HowlerModule | null = null;

type SoundServiceState = {
    sounds: Map<SoundId, Howl[]>;
    lastVariationIndex: Map<SoundId, number>;
    volume: number;
    initialised: boolean;
};

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------

export const clampVolume = (volume: number): number => {
    if (!Number.isFinite(volume)) return DEFAULT_SOUND_VOLUME;
    return Math.min(MAX_SOUND_VOLUME, Math.max(MIN_SOUND_VOLUME, volume));
};

/**
 * Picks a random variation index in [0, count), avoiding `previousIndex` when there is
 * more than one variation to choose from.
 */
export const pickVariationIndex = (
    count: number,
    previousIndex: number | undefined,
    random: () => number = Math.random,
): number => {
    if (count <= 0) return -1;
    if (count === 1) return 0;

    if (previousIndex === undefined || previousIndex < 0 || previousIndex >= count) {
        return Math.floor(random() * count);
    }

    // Pick from the remaining (count - 1) indices, then shift past the previous one
    const offset = Math.floor(random() * (count - 1));
    return offset >= previousIndex ? offset + 1 : offset;
};

// ---------------------------------------------------------------------------
// Volume
// ---------------------------------------------------------------------------

const createInitialState = (): SoundServiceState => ({
    sounds: new Map(),
    lastVariationIndex: new Map(),
    // Volume is fixed for now; setSoundVolume still works for the session if something calls it
    volume: DEFAULT_SOUND_VOLUME,
    initialised: false,
});

let state: SoundServiceState = createInitialState();

export const getSoundVolume = (): number => state.volume;

/**
 * Sets the global sound volume (0..1) for this session.
 * Safe to call before initialisation — the value is applied when the sounds are created.
 */
export const setSoundVolume = (volume: number) => {
    state.volume = clampVolume(volume);
    if (howlerModule) howlerModule.Howler.volume(state.volume);
};

// ---------------------------------------------------------------------------
// Initialisation / preloading
// ---------------------------------------------------------------------------

/**
 * If a play is requested before its sound has finished loading, we wait up to this long for it
 * rather than dropping the sound entirely. Beyond this the sound would land noticeably late and
 * feel disconnected from the interaction.
 */
const MAX_DEFERRED_PLAY_MS = 500;

const createHowl = (howler: HowlerModule, { url }: SoundFile): Howl =>
    new howler.Howl({
        src: [url],
        preload: true,
        onloaderror: (_soundId, error) => logger.warn(`Failed to load sound: ${url}`, error),
        onplayerror: (_soundId, error) => logger.warn(`Failed to play sound: ${url}`, error),
    });

/**
 * Loads howler, creates a Howl per audio file and starts preloading every sound in the manifest.
 * Idempotent — subsequent calls are no-ops. Safe to call in unsupported environments.
 * Never rejects — failures are logged, rolled back so a later call can retry, and sounds
 * simply stay silent in the meantime.
 */
export const initialiseSounds = async (manifest: SoundManifest = SOUND_MANIFEST): Promise<void> => {
    if (state.initialised) return;
    if (typeof window === 'undefined') return;

    state.initialised = true;

    try {
        const startedAt = performance.now();

        // Deferred so the audio library is only downloaded for users with sounds enabled
        howlerModule = await import(/* webpackChunkName: "ux-sounds" */ 'howler');

        // Master volume for all sounds; individual Howls stay at their default volume of 1
        howlerModule.Howler.volume(state.volume);

        const howler = howlerModule;
        (Object.entries(manifest) as [SoundId, SoundFile[]][]).forEach(([soundId, files]) => {
            state.sounds.set(
                soundId,
                files.map((soundFile) => createHowl(howler, soundFile)),
            );
        });

        const allHowls = Array.from(state.sounds.values()).flat();
        await Promise.all(
            allHowls.map(
                (howl) =>
                    new Promise<void>((resolve) => {
                        if (howl.state() === 'loaded') return resolve();
                        howl.once('load', () => resolve());
                        howl.once('loaderror', () => resolve());
                    }),
            ),
        );

        const loadedCount = allHowls.filter((howl) => howl.state() === 'loaded').length;
        logger.info(
            `Loaded ${loadedCount}/${allHowls.length} sound files in ${Math.round(performance.now() - startedAt)}ms`,
        );
    } catch (error) {
        logger.error('Failed to initialise sounds', error);

        // Roll back so a later call can retry (e.g. after a transient chunk-load failure)
        howlerModule?.Howler.unload();
        howlerModule = null;
        state = { ...createInitialState(), volume: state.volume };
    }
};

// ---------------------------------------------------------------------------
// Playback
// ---------------------------------------------------------------------------

/**
 * Plays a sound, picking a variation at random (never the same one twice in a row). Normally
 * immediate (everything is preloaded); if the sound is still loading it plays as soon as it can
 * within MAX_DEFERRED_PLAY_MS, otherwise it's dropped. No-ops if sounds aren't initialised or
 * the volume is zero.
 */
export const playSound = (soundId: SoundId): void => {
    if (!state.initialised) return;
    if (state.volume <= MIN_SOUND_VOLUME) return;

    const variations = state.sounds.get(soundId);
    if (!variations || !variations.length) return;

    const variationIndex = pickVariationIndex(variations.length, state.lastVariationIndex.get(soundId));
    state.lastVariationIndex.set(soundId, variationIndex);
    const howl = variations[variationIndex];

    // Fast path: loaded and ready (Howler resumes a suspended AudioContext itself)
    if (howl.state() === 'loaded') {
        howl.play();
        return;
    }

    // Slow path: wait (briefly) for the load, then play if it's still timely
    const requestedAt = performance.now();
    howl.once('load', () => {
        if (performance.now() - requestedAt > MAX_DEFERRED_PLAY_MS) {
            logger.info(`Dropped late sound: ${soundId}`);
            return;
        }
        howl.play();
    });
};

/** Test-only: resets all module state, unloading every Howl and Howler's shared AudioContext. */
export const resetSoundServiceForTesting = () => {
    howlerModule?.Howler.unload();
    howlerModule = null;
    state = createInitialState();
};
